import React from 'react';
import { Form, FormikProps } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { Alert } from '../../../alert/alert';
import { FairPriceFormValues } from '../../form-values/fair-price-form-values-interface';
import { FairPriceTabDcfParameters } from './parameters/fair-price-tab-dcf-parameters';
import { FairPriceTabDcfResults } from './results/fair-price-tab-dcf-results';
import { FairPriceTabDcfStats } from './stats/fair-price-tab-dcf-stats';

interface FairPriceTabDcfProps {
    formikProps: FormikProps<FairPriceFormValues>;
}

export const FairPriceTabDcf: React.FC<FairPriceTabDcfProps> = observer(({ formikProps }) => {
    const { t } = useTranslation();
    const {
        symbolsStore: { symbolFairPriceStore, symbolDetailStore },
    } = useRootStore();
    const { currentPrice, intrinsicValueDCF, fairPriceDCF, upsideDownsideDCF, terminalValueWeight } =
        symbolFairPriceStore;
    const currency = symbolDetailStore?.general?.symbol?.currency || 'USD';
    const { inputDiscountRate, inputTerminalGrowthRate } = symbolFairPriceStore;
    const isInvalidRate =
        inputDiscountRate != null && inputTerminalGrowthRate != null && inputDiscountRate <= inputTerminalGrowthRate;
    const showResult = !isInvalidRate && fairPriceDCF > 0 && currentPrice > 0;

    if (!currentPrice) {
        return (
            <div>
                <Alert type={'warning'} icon>
                    {t('layout.dialogs.fairPrice.dcf.noData')}
                </Alert>
            </div>
        );
    }

    return (
        <Form id={'fair-price-form'}>
            <FairPriceTabDcfStats
                currency={currency}
                totalFcf={symbolFairPriceStore.apiDcfTotalFcf}
                cash={symbolFairPriceStore.apiDcfCash}
                debt={symbolFairPriceStore.apiDcfDebt}
                sharesOutstanding={symbolFairPriceStore.apiDcfSharesOutstanding}
            />
            <FairPriceTabDcfParameters formikProps={formikProps} showResult={showResult} />
            <FairPriceTabDcfResults
                currency={currency}
                currentPrice={currentPrice}
                intrinsicValue={intrinsicValueDCF}
                fairPrice={fairPriceDCF}
                upsideDownside={upsideDownsideDCF}
                terminalValueWeight={terminalValueWeight}
                isInvalidRate={isInvalidRate}
                showResult={showResult}
            />
        </Form>
    );
});
